import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserContext } from '../../../../context/UserContext';
import { useHistory, useLocation } from 'react-router-dom';
import VideoService from '../../../../service/VideoService';
import useFetchListWithInfiniteScroll from '../../../../hooks/useFetchListWithInfiniteScroll';
import useFetchFoldersList from '../../../../hooks/useFetchFoldersList';
import { useLibraryContext } from '../../../../context/LibraryContext';
import { AnimationWrapper } from '../../../../components/AnimationWrapper/AnimationWrapper';
import styles from './FolderVideos.module.scss';
import ContentPageHeader from '../../../../components/ContentPageHeader/ContentPageHeader';
import Row from '../../../../components/GridSystem/Row';
import cx from 'classnames';
import DashboardCards from '../../../Dashboard/components/DashboardCards/DashboardCards';
import RecordNotFound from '../../../../components/_empty_state/RecordNotFound';
import RecordingActions from '../../../Dashboard/components/RecordingActions/RecordingActions';
import { useTranslation } from 'react-i18next';
import ShareFolderModal from '../../../../components/ShareFolderModal/ShareFolderModal';
import { FOLDER_USER_ROLE } from '../../../../constants/UserRoles';
import { approvalStatusesForFilter } from '../../../Dashboard/components/VideosFilter/constants';
import MobileSearchModal from '../../../../components/MobileSearchModal/MobileSearchModal';
import SearchModalContent from '../../../../components/SearchModalContent/SearchModalContent';
import UploadVideoModal from '../../../../components/UploadVideoModal/UploadVideoModal';

import MobileHeader from '../../../../components/MobileHeader';
import Content from '../../../../components/Content/Content';
import RightSidebarMenu from '../../../../components/RightVideoActionMenu/RightSidebarMenu';
import { useResponsiveContext } from '../../../../context/ResponsiveContext';
import LocalStorageService from '../../../../service/LocalStorageService';
import CmsAPI from '../../../../../common/apis/CmsAPI';
import MobileUploadButton from '../../../../components/MobileUploadButton/MobileUploadButton';

const viewModeToken = { GRID_VIEW: 'GRID_VIEW', LIST_VIEW: 'LIST_VIEW' };
const viewModeInit = viewModeToken.GRID_VIEW;

const FolderVideos = ({ folderId }) => {
  const { user, userLoading, isApprovalFlowEnabled } = useUserContext();
  const [folder, setFolder] = useState('');
  const [folderLoading, setFolderLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState({
    TYPE: approvalStatusesForFilter.ALL_VIDEOS.TYPE,
    DESCRIPTION: approvalStatusesForFilter.ALL_VIDEOS.DESCRIPTION,
  });

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const [searchValue, setSearchValue] = useState({ query: '', tags: [] });
  const [isShowUploadVideoModal, setIsShowUploadVideoModal] = useState(false);

  const [viewMode, setViewMode] = useState(viewModeInit);
  const [isArchive, setIsArchive] = useState(false);
  const [checkingForArchive, setCheckingForArchive] = useState(true);
  const isListView = useMemo(() => viewMode === viewModeToken.LIST_VIEW, [viewMode]);
  const [searchModal, setSearchModal] = useState(false);

  const { state } = useLocation();
  const history = useHistory();

  const { t } = useTranslation();

  const onInputChange = useCallback(
    value => {
      if (typeof value === 'string') {
        setSearchValue(prevState => ({ ...prevState, query: value }));
        if (state?.folders || state?.team) {
          searchInputFolders.onChange(value);
          return;
        }
        searchInputVideos.onChange('searchTerm', value);
      isMobileView && searchInputFolders.onChange(value);

      } else {
        setSearchValue(prevState => ({ ...prevState, tags: value }));
        searchInputVideos.onChange(
          'tags',
          value.map(item => item.id)
        );
      }
    },
    [state]
  );

  const { userId, userFullName } = useMemo(() => {
    if (user) {
      const fullName = user.userFullName;
      const userId = user.username;
      return { userId, userFullName: fullName };
    }
    return {};
  }, [user]);

  const handleChangeApprovalFilter = value => {
    if (selectedFilter.TYPE === value.TYPE) {
      return;
    }

    setVideos([]);
    setTotalData(0);
    setSelectedFilter(value);
    onApprovalStatusFilter(value.TYPE);
    toggleUnselectCards();
    history.replace({ state: { ...state, filter: value } });
  };

  const isFolderOwner = userId === folder?.owner?.username;
  const isFolderEditable = !!folder?.editingEnabled;
  const isEditor = useMemo(
    () => folder?.users?.find(({ username }) => username === userId)?.folderRole === FOLDER_USER_ROLE.EDITOR.TYPE ?? false,
    [folder]
  );

  const isUserHasAdvancedEditingRights = isFolderOwner || (isEditor && isFolderEditable);

  const getVideosWithStats = useCallback(
    async query => {
      query.folder = folderId;
      query.archived = isArchive;
      query.include_empty = true;
      if (query.folder) {
        try {
          const response = await VideoService.getVideos(query);

          return new Promise(function(resolve) {
            return resolve(response);
          });
        } catch (err) {
          return new Promise(function(resolve, reject) {
            return reject(err);
          });
        }
      }
    },
    [folderId]
  );

  const {
    infiniteRef,
    list: videos,
    setList: setVideos,
    totalData,
    isEmptyList: isVideosEmpty,
    loading,
    searchInput: searchInputVideos,
    query,
    setTotalData,
    loadAllList,
    onApprovalStatusFilter,
  } = useFetchListWithInfiniteScroll({ service: checkingForArchive || isArchive ? null : getVideosWithStats });

  const {
    list: folders,
    loadList: loadFolders,
    searchInput: searchInputFolders,
    sharedFolderWithUsers,
    editFolderUsers,
    deleteFolderUsers,
    changeFolderEditingRight,
    usersOutsideCompany,
    setUsersOutsideCompany,
    isShowShareFolderModal,
    setIsShowShareFolderModal,
  } = useFetchFoldersList({ archived: isArchive });

  const folderUsers = useMemo(() => {
    if (folderLoading) {
      return [];
    } else {
      return [folder?.owner ?? { name: t('folder.deleted_user') }, ...(folder?.users ?? [])];
    }
  }, [folderLoading, folder]);

  const setVideosAndTotalData = useCallback(
    updatedVideos => {
      setVideos(updatedVideos);
      setTotalData(totalData - 1);
    },
    [setTotalData, setVideos, totalData]
  );

  useEffect(() => {
    if (state?.filter !== null && state?.filter !== undefined) {
      handleChangeApprovalFilter(state.filter);
    }
  }, []);

  const {
    showRecordingActions,
    selectedCard,
    selectedFolder,
    disableShowRecordingActions,
    toggleUnselectCards,
    isStoryboardGeneratorEnabled,
  } = useLibraryContext();

  useEffect(() => {
    if (folders.length > 0) {
      const currentFolder = folders.find(({ id }) => id === folderId);
      setFolder(currentFolder);

      if (!currentFolder) {
        history.push('/eg/404');
      } else {
        setFolderLoading(false);
      }
    }
  }, [folders]);

  const showShareFolderModal = () => {
    setIsShowShareFolderModal(true);
  };

  const handleCloseShareModal = () => {
    setIsShowShareFolderModal(false);
    setUsersOutsideCompany([]);
  };

  const handleNewProject = () => {
    disableShowRecordingActions();
    history.push('/eg/storyboard/new', { folder: folderId, create: !isStoryboardGeneratorEnabled });
  };

  const handleSetListView = useCallback(() => {
    if (!isListView) {
      setViewMode(viewModeToken.LIST_VIEW);
      LocalStorageService.setItem('ViewMode', viewModeToken.LIST_VIEW);
    }
  }, [isListView]);

  const handleSetGridView = useCallback(() => {
    if (isListView) {
      setViewMode(viewModeToken.GRID_VIEW);
      LocalStorageService.setItem('ViewMode', viewModeToken.GRID_VIEW);
    }
  }, [isListView]);

  const { isMdAndBelow } = useResponsiveContext();
  const selectedVideoCount = Object.keys(selectedCard).length + Object.keys(selectedFolder).length;

  useEffect(() => {
    const savedViewMode = LocalStorageService.getItem('ViewMode');

    if (savedViewMode) {
      setViewMode(savedViewMode);
    }

    const checkIsFolderArchived = async () => {
      try {
        const { result } = await CmsAPI.getFolder(folderId);
        setIsArchive(!!result.archived);
      } catch (err) {
        console.log(err);
      } finally {
        setCheckingForArchive(false);
      }
    };

    checkIsFolderArchived();
  }, []);

  const closeSearchModal = () => {
    setSearchModal(false);
  };

  const openSearchModal = () => {
    setSearchModal(true);
  };

  return (
    <>
      <MobileHeader
        libraryMode
        userAvatar={user?.avatarUri}
        userFirstLetter={user?.userFullName.charAt(0).toUpperCase()}
        openSearchModal={openSearchModal}
      />
      <Content className={styles.content}>
        <>
          {!userLoading && !checkingForArchive && (
            <Row className={cx(styles.dashboard)}>
              <div className={styles.dashboard}>
                <ContentPageHeader
                  userFullName={userFullName}
                  isMdAndBelow={isMdAndBelow}
                  isListView={isListView}
                  handleSetGridView={handleSetGridView}
                  handleSetListView={handleSetListView}
                  searchValue={searchValue}
                  handleInputChange={onInputChange}
                  showShareFolderModal={isUserHasAdvancedEditingRights && showShareFolderModal}
                  title={folder?.name}
                  isFolderEditable={!!folder?.editingEnabled}
                  folderUsers={folderUsers}
                  mode="FOLDER"
                  isNewVideosAllowed={isFolderOwner || isEditor}
                />

                {isMobileView && (
                  <div className={styles.uploadButton}>
                    <MobileUploadButton setIsShowUploadVideoModal={setIsShowUploadVideoModal} />
                  </div>
                )}

                {folder && (
                  <DashboardCards
                    searchInput={searchValue}
                    videos={videos}
                    listMode={isListView}
                    loadVideos={loadAllList}
                    loadFolders={loadFolders}
                    setVideosAndTotalData={setVideosAndTotalData}
                    folder={folder}
                    isUserHasEditingRights={isFolderOwner || isEditor}
                    isUserHasAdvancedEditingRights={isUserHasAdvancedEditingRights}
                    archived={isArchive}
                    onDuplicateSucceeded={() => history.push(`/eg/dashboard`, { folders: false, team: false })}
                    query={query}
                    loading={loading}
                    isVideosEmpty={isVideosEmpty}
                    infiniteRef={infiniteRef}
                    selectedFilter={selectedFilter}
                    handleChangeApprovalFilter={handleChangeApprovalFilter}
                    isApprovalFlowEnabled={isApprovalFlowEnabled}
                    totalData={checkingForArchive ? 0 : totalData}
                    folderVideos={true}
                  />
                )}

                {!loading && query.searchTerm && videos?.length === 0 && !isVideosEmpty && folders?.length === 0 && (
                  <RecordNotFound searchTerm={query.searchTerm} />
                )}
                <AnimationWrapper show={showRecordingActions} from={{ opacity: 0 }} to={{ opacity: 1 }} options={{ duration: 100 }}>
                  <RecordingActions
                    disableShowRecordingActions={disableShowRecordingActions}
                    folderId={folderId}
                    onRedirect={handleNewProject}
                    handleShowUploadVideoModal={() => setIsShowUploadVideoModal(true)}
                  />
                </AnimationWrapper>
                <UploadVideoModal
                  isShow={isShowUploadVideoModal}
                  onClose={() => setIsShowUploadVideoModal(false)}
                  loadVideos={loadAllList}
                  folderId={folderId}
                />
                {/* This modal will show after clicking on the edit icon in the top bar */}
                {isShowShareFolderModal && (
                  <ShareFolderModal
                    sharedFolderWithUsers={sharedFolderWithUsers}
                    editFolderUsers={editFolderUsers}
                    deleteFolderUsers={deleteFolderUsers}
                    changeFolderEditingRight={changeFolderEditingRight}
                    onClose={handleCloseShareModal}
                    isShow={isShowShareFolderModal}
                    folder={folder}
                    usersOutsideCompany={usersOutsideCompany}
                  />
                )}
                <MobileSearchModal open={searchModal} onClose={closeSearchModal}>
                  <SearchModalContent onClose={closeSearchModal} selectedFilter={selectedFilter} folderId={folderId} />
                </MobileSearchModal>
              </div>
            </Row>
          )}
          {selectedVideoCount > 0 ? (
            <RightSidebarMenu folderId={folderId} userId={user.username} selectedCards={selectedVideoCount} isArchive={isArchive} />
          ) : null}
        </>
      </Content>
    </>
  );
};

export default FolderVideos;
